import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ExpertSearchRow from '../../components/searchExpertsComponents/ExpertSearchRow';
import Dropdown from '../../components/Dropdown';
import { ROUTES } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { followApi } from '../../services/followService';
import { tipsterApi, TipsterFilters } from '../../services/tipsterService';
import { TipsterPublic } from '../../types/interfaces';

const SearchExperts: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tipsters, setTipsters] = useState<TipsterPublic[]>([]);
  const [followedIds, setFollowedIds] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<TipsterFilters>({
    followingOnly: false,
    tagSearch: '',
    sortBy: 'followers',
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    const v = searchParams.get('followingOnly');
    if (v === '1' || v === 'true') {
      setFilters((prev) => ({ ...prev, followingOnly: true }));
    }
  }, [searchParams]);

  const fetchTipsters = async () => {
    try {
      const [tipstersData, ids] = await Promise.all([
        tipsterApi.getAll(filters),
        followApi.getFollowedIds().catch(() => [] as number[]),
      ]);
      setTipsters(tipstersData);
      setFollowedIds(new Set(ids));
    } catch (error) {
      console.error('Failed to fetch tipsters:', error);
    }
  };

  useEffect(() => {
    fetchTipsters();
  }, [filters]);

  const handleToggleFollow = async (tipsterId: number, currentlyFollowing: boolean) => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    try {
      if (currentlyFollowing) {
        await followApi.unfollow(tipsterId);
        setFollowedIds(prev => {
          const next = new Set(prev);
          next.delete(tipsterId);
          return next;
        });
        setTipsters(prev =>
          prev.map(t => t.id === tipsterId ? { ...t, followers_count: t.followers_count - 1 } : t)
        );
      } else {
        await followApi.follow(tipsterId);
        setFollowedIds(prev => new Set(prev).add(tipsterId));
        setTipsters(prev =>
          prev.map(t => t.id === tipsterId ? { ...t, followers_count: t.followers_count + 1 } : t)
        );
      }
    } catch {
      // silently fail
    }
  };

  const handleTagSearch = () => {
    setFilters(prev => ({ ...prev, tagSearch: tagInput }));
  };

  const sortOptions = [
    { value: 'followers', label: t.experts.sortFollowers },
    { value: 'appreciation', label: t.experts.sortAppreciation },
    { value: 'recommendations', label: t.experts.sortCurrentPicks },
  ];

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTagSearch();
    }
  };

  return (
    <div className="search-experts">
      <Helmet>
        <title>Find Expert Sports Tipsters | PhilipSnat</title>
        <meta name="description" content="Discover and follow verified sports betting experts on PhilipSnat. Browse tipsters by sport, win rate, ROI, and followers. Find the best picks for NHL, KHL, and more." />
        <meta property="og:title" content="Find Expert Sports Tipsters | PhilipSnat" />
        <meta property="og:description" content="Discover and follow verified sports betting experts on PhilipSnat. Browse tipsters by sport, win rate, ROI, and followers." />
        <meta property="og:url" content="https://philipsnat.com/experts/search" />
        <link rel="canonical" href="https://philipsnat.com/experts/search" />
      </Helmet>
      <h1 className="page-header-title page-header-title--stacked italic-title">{t.experts.searchTitle}</h1>

      <div className="search-experts__filters" role="search" aria-label={t.experts.expertFilters}>
        <div className="search-experts__filters-toolbar">
          <label className="search-experts__switch">
            <input
              type="checkbox"
              className="sr-only"
              checked={filters.followingOnly}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked && !isAuthenticated) {
                  navigate(
                    `${ROUTES.LOGIN}?redirect=${encodeURIComponent(location.pathname + location.search)}`
                  );
                  return;
                }
                setFilters((prev) => ({ ...prev, followingOnly: checked }));
              }}
            />
            <span className="search-experts__switch-track" aria-hidden />
            <span className="search-experts__switch-text">{t.filters.followingTipstersOnly}</span>
          </label>

          <div className="search-experts__tag-row">
            <input
              type="search"
              enterKeyHint="search"
              placeholder={t.experts.tagPlaceholder}
              className="login__input search-experts__tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
            />
            <button
              type="button"
              className="button_primary search-experts__tag-button"
              onClick={handleTagSearch}
            >
              {t.common.search}
            </button>
          </div>

          <div className="generator__market-field search-experts__sort-field">
            <label className="generator__field-label" htmlFor="search-experts-sort">
              {t.common.sort}
            </label>
            <Dropdown
              id="search-experts-sort"
              className="filters__select"
              value={filters.sortBy ?? 'followers'}
              onChange={(sortBy) =>
                setFilters((prev) => ({ ...prev, sortBy: sortBy as TipsterFilters['sortBy'] }))
              }
              options={sortOptions}
              title={t.common.sort}
              placeholder={t.common.sort}
            />
          </div>
        </div>
      </div>

      {tipsters.map((tipster) => (
        <ExpertSearchRow
          key={tipster.id}
          tipster={tipster}
          isFollowing={followedIds.has(tipster.id)}
          onToggleFollow={handleToggleFollow}
          showRoi
        />
      ))}
    </div>
  );
};

export default SearchExperts;
